import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom'
import { getKata, type KataId } from '../data/catalog'
import { getKataScoring, isScoringSupported } from '../data/scoring'
import { kataBannerBackground } from '../lib/theme/kataBanner'
import { LanguageSwitcher } from '../components/i18n/LanguageSwitcher'
import {
  ScoringCriteriaSourceNotice,
  ScoringDisclaimer,
} from '../components/scoring/ScoringDisclaimer'
import { useI18n } from '../i18n'
import {
  createPerformance,
  deletePerformance,
  findLatestDraft,
  listPerformancesForKata,
} from '../storage/repositories/performances'
import type { KataPerformance } from '../storage/schema'
import styles from './KataScoring.module.css'

function formatScore(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toFixed(1)
}

function formatDate(iso: string, locale: string): string {
  try {
    return new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(new Date(iso))
  } catch {
    return iso.slice(0, 10)
  }
}

export function KataPerformances() {
  const { t, locale } = useI18n()
  const navigate = useNavigate()
  const { kataId } = useParams<{ kataId: string }>()
  const kata = kataId ? getKata(kataId as KataId) : undefined
  const scoring = kata ? getKataScoring(kata.id) : undefined

  const [rows, setRows] = useState<KataPerformance[] | null>(null)
  const [error, setError] = useState(false)
  const [busy, setBusy] = useState(false)

  const reload = useCallback(async () => {
    if (!kata || !isScoringSupported(kata.id)) return
    try {
      setRows(await listPerformancesForKata(kata.id))
      setError(false)
    } catch {
      setError(true)
      setRows([])
    }
  }, [kata])

  useEffect(() => {
    void reload()
  }, [reload])

  const completed = useMemo(
    () => (rows ?? []).filter((r) => r.status === 'completed').slice().reverse(),
    [rows],
  )

  const latestCompleted = useMemo(() => {
    return (rows ?? []).find((r) => r.status === 'completed')
  }, [rows])

  const previousCompleted = useMemo(() => {
    const done = (rows ?? []).filter((r) => r.status === 'completed')
    return done.length >= 2 ? done[1] : undefined
  }, [rows])

  const chartPoints = useMemo(() => {
    return completed.slice(-12)
  }, [completed])

  const maxTotal = scoring?.maxPerJudge ?? 180

  if (!kata) return <Navigate to="/" replace />
  if (!isScoringSupported(kata.id) || !scoring) {
    return (
      <div className="app-shell">
        <p className="banner banner-info" role="status">
          {t('scoring.unsupported')}
        </p>
        <Link to={`/kata/${kata.id}`} className="btn btn-secondary">
          {t('scoring.backPractice')}
        </Link>
      </div>
    )
  }

  async function startNew() {
    if (!kata) return
    setBusy(true)
    try {
      const draft = await findLatestDraft(kata.id)
      if (draft) {
        navigate(`/kata/${kata.id}/judge?performance=${draft.id}`)
        return
      }
      const created = await createPerformance(kata.id)
      navigate(`/kata/${kata.id}/judge?performance=${created.id}`)
    } catch {
      setError(true)
    } finally {
      setBusy(false)
    }
  }

  async function forceNew() {
    if (!kata) return
    setBusy(true)
    try {
      const created = await createPerformance(kata.id)
      navigate(`/kata/${kata.id}/judge?performance=${created.id}`)
    } catch {
      setError(true)
    } finally {
      setBusy(false)
    }
  }

  async function remove(id: string) {
    if (!window.confirm(t('scoring.deleteConfirm'))) return
    setBusy(true)
    try {
      await deletePerformance(id)
      await reload()
    } catch {
      setError(true)
    } finally {
      setBusy(false)
    }
  }

  const delta =
    latestCompleted && previousCompleted
      ? latestCompleted.finalTotal - previousCompleted.finalTotal
      : null

  return (
    <div className="app-shell">
      <header className={styles.topBar}>
        <div className={styles.topRow}>
          <Link to={`/kata/${kata.id}`} className={`btn btn-ghost ${styles.topBack}`}>
            {t('scoring.backPractice')}
          </Link>
          <LanguageSwitcher compact className={styles.topLang} />
        </div>
        <h1 className={styles.pageTitle}>
          <span lang="ja">{kata.nameJa}</span>
          <span className={styles.pageRomaji}>{kata.name}</span>
        </h1>
      </header>

      <div
        className={styles.hero}
        style={{
          background: kataBannerBackground(kata.theme),
          color: kata.theme.text,
        }}
      >
        <p className={styles.heroLabel}>{t('scoring.hubLabel')}</p>
        <p className={styles.heroTitle}>{t('scoring.hubTitle')}</p>
        {latestCompleted ? (
          <p className={styles.heroMeta}>
            {t('scoring.latest', {
              score: formatScore(latestCompleted.finalTotal),
              max: maxTotal,
            })}
            {delta !== null
              ? ` · ${
                  delta >= 0
                    ? t('scoring.deltaUp', { delta: formatScore(delta) })
                    : t('scoring.deltaDown', { delta: formatScore(delta) })
                }`
              : ''}
          </p>
        ) : null}
      </div>

      <ScoringCriteriaSourceNotice />

      {error ? (
        <p className="banner banner-error" role="alert">
          {t('scoring.loadError')}
        </p>
      ) : null}

      <div className={styles.actions}>
        <button
          type="button"
          className="btn btn-primary"
          disabled={busy}
          onClick={() => void startNew()}
        >
          {t('scoring.newPerformance')}
        </button>
        <button
          type="button"
          className="btn btn-secondary"
          disabled={busy}
          onClick={() => void forceNew()}
        >
          {t('scoring.duplicateNew')}
        </button>
      </div>

      <section className={`surface ${styles.section}`}>
        <h2 className={styles.sectionTitle}>{t('scoring.improvement')}</h2>
        {chartPoints.length < 2 ? (
          <p className={styles.chartHint}>{t('scoring.improvementEmpty')}</p>
        ) : (
          <>
            <div className={styles.chart} role="img" aria-label={t('scoring.improvement')}>
              {chartPoints.map((p) => {
                const height = Math.max(4, (p.finalTotal / maxTotal) * 100)
                return (
                  <button
                    key={p.id}
                    type="button"
                    className={styles.chartBar}
                    style={{ height: `${height}%` }}
                    title={`${formatDate(p.completedAt ?? p.createdAt, locale)} · ${formatScore(p.finalTotal)}`}
                    onClick={() => navigate(`/kata/${kata.id}/judge?performance=${p.id}`)}
                  />
                )
              })}
            </div>
            <p className={styles.chartHint}>0 … {maxTotal}</p>
          </>
        )}
      </section>

      <section className={`surface ${styles.section}`}>
        <h2 className={styles.sectionTitle}>{t('scoring.past')}</h2>
        {!rows ? (
          <p className="banner banner-info">{t('common.loading')}</p>
        ) : rows.length === 0 ? (
          <p className={styles.chartHint}>{t('scoring.empty')}</p>
        ) : (
          <ul className={styles.list}>
            {rows.map((p) => (
              <li key={p.id}>
                <div className={styles.row}>
                  <button
                    type="button"
                    className="btn btn-ghost"
                    style={{ flex: '1 1 auto', justifyContent: 'flex-start', paddingInline: 0 }}
                    onClick={() => navigate(`/kata/${kata.id}/judge?performance=${p.id}`)}
                  >
                    <span>
                      {formatDate(p.completedAt ?? p.updatedAt, locale)}
                      {p.scoredBy ? ` · ${p.scoredBy}` : p.status === 'draft' ? ` · ${t('scoring.statusDraft')}` : ''}
                    </span>
                    <span className={styles.rowScore}>
                      {formatScore(p.finalTotal)}/{p.maxTotal}
                    </span>
                  </button>
                  <div className={styles.rowActions}>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => navigate(`/kata/${kata.id}/judge?performance=${p.id}`)}
                    >
                      {p.status === 'draft' ? t('scoring.resume') : t('scoring.open')}
                    </button>
                    <button
                      type="button"
                      className="btn btn-ghost"
                      disabled={busy}
                      onClick={() => void remove(p.id)}
                    >
                      {t('scoring.delete')}
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <ScoringDisclaimer />
    </div>
  )
}
